import { t } from "i18next";
import moment from "moment";
import { useSelector } from "react-redux";

import deficiencyTypes from "../../data/deficiency_types.json";
import { useRedirect } from "../../hooks/useRedirect";
import { RootState } from "../../store";
import { Comment } from "../../types/comment";
import { Deficiency } from "../../types/deficiencies";

import styles from "./deficiency-card.module.css";

type Tag = "mangel" | "geteilt" | "none";

type DeficiencyCardProps = {
  deficiency: Deficiency;
  onClick?: () => void;
  propertyAreaLabel?: string;
  showDueDate?: boolean;
  tag?: Tag;
};

export const DeficiencyCard = ({
  deficiency,
  onClick,
  propertyAreaLabel,
  showDueDate = false,
  tag,
}: DeficiencyCardProps) => {
  const { setDashboardUrlAndRedirect } = useRedirect();

  const dateMoment = showDueDate
    ? moment(deficiency.dueDate)
    : moment(deficiency.createdAt);
  const date = dateMoment.format("DD.MM.");
  const time = dateMoment.format("HH:mm");

  const deficiencyType = deficiencyTypes.find(
    (type) => type.deficiencyType === deficiency.deficiencyType
  );

  const responsibilityText =
    deficiency.responsibility === "exterminator" ? "SBK" : t("Customer");

  const isCustomerAdmin = useSelector(
    (state: RootState) => state.user.isCustomerAdmin
  );

  const hasSystemComment = deficiency.includes?.comments?.some(
    (comment: Comment) => comment.isSystem === 1
  );

  const computedTag: Tag =
    tag !== undefined
      ? tag
      : isCustomerAdmin
        ? hasSystemComment
          ? "geteilt"
          : "none"
        : "mangel";

  const handleCardClick = () => {
    if (onClick) {
      onClick();
      return;
    }

    setDashboardUrlAndRedirect(`/deficiencies/${deficiency.id}/show`, {
      deficiencyDate: deficiency.createdAt,
    });
  };

  return (
    <>
      <div className={styles["card"]} onClick={handleCardClick}>
        <div className={`${styles["card__circle"]}`}>{date}</div>

        <div className={styles["card__info"]}>
          <div className={styles["card__info__title"]}>
            {deficiency.responsibility === "exterminator" && <i>{time}</i>}
            <span>{deficiencyType?.name ?? "N/A"}</span>
          </div>

          <div className={styles["card__info__subtitle"]}>
            {propertyAreaLabel ? `${propertyAreaLabel}, ` : ""}
            {`${deficiency.propertyAreaLabel ?? ""}${
              deficiency.propertyAreaLabel ? ", " : ""
            }${deficiency.location ?? "/"}`}
          </div>

          <div className={`${styles["card__tags"]}`}>
            <p className={`${styles["card__type--responsibility"]}`}>
              {responsibilityText}
            </p>
            {computedTag === "mangel" && (
              <p className={`${styles["card__type"]}`}>{t("Deficiency")}</p>
            )}
            {computedTag === "geteilt" && (
              <p className={`${styles["card__type--responsibility"]}`}>
                {t("Shared")}
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
